import { Button } from '@/components/shadcn/button'
import { useRestClient } from '@/contexts/QueryProvider'
import { SectionContainer } from '@/pages/shared-components/SectionContainer'
import { toast } from 'sonner'
import { useCurrentWorkspace } from '../useCurrentWorkspace'
import { ENDPOINTS } from '@/util'
import { useSessionStorage } from '@/hooks/useSessionStorage'
import { useState } from 'react'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/shadcn/dialog'

import { getDatabricksErrorMessage } from '@/lib/databricksErrorMessage'

export function DataBricksActionsSection() {
  const { workspaceId, project, organisation_id, project_id, app_key } = useCurrentWorkspace()
  const [token] = useSessionStorage('token', '')
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [deleteResponse, setDeleteResponse] = useState<string[] | null>(null)
  const {
    getDatabricksApiGetDatabricksWorkspaceStatus,
    putDatabricksApiCreateDatabricksWorkspace,
    putDatabricksApiSyncDatabricksWorkspaceData,
    putDatabricksApiSyncDatabricksResultsToWorkspace,
    postDatabricksApiDeleteDatabricksWorkspace,
  } = useRestClient()

  const invalidateWorkspace = () => getDatabricksApiGetDatabricksWorkspaceStatus.invalidateQueries()

  const { mutate: provisionWorkspace, isPending: isProvisioningWorkspace } =
    putDatabricksApiCreateDatabricksWorkspace.useMutation()
  const { mutate: syncWorkspaceData, isPending: isSyncingWorkspaceData } =
    putDatabricksApiSyncDatabricksWorkspaceData.useMutation()
  const { mutate: syncWorkspaceResults, isPending: isSyncingWorkspaceResults } =
    putDatabricksApiSyncDatabricksResultsToWorkspace.useMutation()
  const { mutate: deleteWorkspace, isPending: isDeletingWorkspace } =
    postDatabricksApiDeleteDatabricksWorkspace.useMutation(undefined, {
      onSuccess: (data) => {
        setDeleteResponse(data ?? [])
        setDeleteConfirmOpen(false)
        invalidateWorkspace()
      },
      onError: (error) => {
        toast.error('Error deleting workspace: ' + getDatabricksErrorMessage(error))
        setDeleteConfirmOpen(false)
      },
      onSettled: () => {
        invalidateWorkspace()
      },
    })
  const [isExportingPowerBi, setIsExportingPowerBi] = useState(false)

  const onDeleteWorkspace = () => {
    deleteWorkspace({
      query: {
        organisationId: String(organisation_id),
        projectId: String(project_id),
        applicationKey: app_key,
      },
    })
  }

  const onProvisionWorkspace = () => {
    toast.info('Started provisioning workspace')

    provisionWorkspace(
      { query: { workspaceId: workspaceId } },
      {
        onError: (error) => {
          toast.error('Error provisioning workspace: ' + getDatabricksErrorMessage(error))
        },
        onSuccess: () => {
          toast.success('Provisioned workspace')
        },
        onSettled: () => {
          invalidateWorkspace()
        },
      }
    )
  }

  const onSyncData = () => {
    toast.info('Started syncing data')

    syncWorkspaceData(
      { query: { workspaceId: workspaceId } },
      {
        onError: (error) => {
          toast.error('Error syncing data: ' + getDatabricksErrorMessage(error))
        },
        onSuccess: () => {
          toast.success("Sync'd data")
        },
        onSettled: () => {
          //invalidateWorkspace()
        },
      }
    )
  }

  const onSyncResults = () => {
    toast.info('Started syncing results')

    syncWorkspaceResults(
      { query: { workspaceId: workspaceId } },
      {
        onError: (error) => {
          toast.error('Error syncing results: ' + getDatabricksErrorMessage(error))
        },
        onSuccess: () => {
          toast.success("Sync'd results")
        },
        onSettled: () => {
          //invalidateWorkspace()
        },
      }
    )
  }

  const onPowerBi = async () => {
    if (!token) {
      toast.error('No token found')
      return
    }
    setIsExportingPowerBi(true)
    const response = await fetch(`${ENDPOINTS.LANDING}/databricks/workspace/powerbi?workspaceId=${workspaceId}`, {
      headers: { Authorization: 'Bearer ' + token },
    })
    if (response.ok) {
      const blob = await response.blob()
      const objectUrl = window.URL.createObjectURL(blob)

      const filename = `workspace-${project}-${workspaceId}.pbids`

      const anchor = document.createElement('a')
      document.body.appendChild(anchor)
      anchor.setAttribute('href', objectUrl)
      anchor.setAttribute('download', filename)
      anchor.click()
      window.URL.revokeObjectURL(objectUrl)
      setIsExportingPowerBi(false)
    } else {
      let errMessage = 'Unknown error'
      if (response.statusText) errMessage = response.statusText
      try {
        const tryJson = await response.json()
        if (tryJson.Message) errMessage = tryJson.Message
      } catch (_error) {
        // ignore
      }
      toast.error('Error downloading file')
      setIsExportingPowerBi(false)
    }
  }

  return (
    <SectionContainer title="Actions">
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onOpenChange={(open) => {
          setDeleteConfirmOpen(open)
          if (!open) setDeleteResponse(null)
        }}
        title="Delete workspace"
        description="Are you sure you want to delete this Databricks workspace and all associated resources? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmVariant="destructive"
        onConfirm={onDeleteWorkspace}
        isSubmitting={isDeletingWorkspace}
      />
      <Dialog open={deleteResponse !== null} onOpenChange={(open) => !open && setDeleteResponse(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete workspace result</DialogTitle>
            <DialogDescription asChild>
              <div className="mt-2">
                <pre className="max-h-60 overflow-auto whitespace-pre-wrap rounded bg-muted p-3 text-sm">
                  {deleteResponse?.length ? deleteResponse.join('\n\n') : 'No feedback items.'}
                </pre>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setDeleteResponse(null)}>Dismiss</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* <div className="flex shrink-0 flex-row flex-wrap gap-x-20 gap-y-4"> */}
      <div className="grid w-full grid-cols-[repeat(auto-fill,minmax(350px,1fr))] gap-1">
        <Button
          variant="outline"
          className="flex h-auto flex-col gap-0 py-3"
          disabled={isProvisioningWorkspace}
          onClick={() => onProvisionWorkspace()}
        >
          <span>Provision Workspace</span>
          <span className="whitespace-normal text-xs font-light italic">
            Set up folder on Databricks and copy app notebook(s)
          </span>
        </Button>
        <Button
          variant="outline"
          className="flex h-auto flex-col gap-0 py-3"
          disabled={isDeletingWorkspace}
          onClick={() => setDeleteConfirmOpen(true)}
        >
          <span>Delete workspace</span>
          <span className="whitespace-normal text-xs font-light italic">
            Remove Databricks workspace and all associated resources
          </span>
        </Button>
        <Button
          variant="outline"
          className="flex h-auto flex-col gap-0 py-3"
          disabled={isSyncingWorkspaceData}
          onClick={() => onSyncData()}
        >
          <span>Sync data</span>
          <span className="whitespace-normal text-xs font-light italic">Synchronize approved inputs</span>
        </Button>
        <Button
          variant="outline"
          className="flex h-auto flex-col gap-0 py-3"
          disabled={isSyncingWorkspaceResults}
          onClick={() => onSyncResults()}
        >
          <span>Sync results</span>
          <span className="whitespace-normal text-xs font-light italic">Synchronize outputs from Databricks</span>
        </Button>
        <Button
          variant="outline"
          className="flex h-auto flex-col gap-0 py-3"
          disabled={isExportingPowerBi}
          onClick={() => onPowerBi()}
        >
          <span>Export .pbids</span>
          <span className="whitespace-normal text-xs font-light italic">
            Download a PowerBI datasource connection file
          </span>
        </Button>
      </div>
    </SectionContainer>
  )
}
